import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useAuth } from './AuthContext';
import { useDeliveryCockpitTour, markDeliveryCockpitTourSessionSeen } from '../hooks/useDeliveryCockpitTour';
import { usePendingTimesheetApprovalCount } from '../hooks/usePendingTimesheetApprovalCount';
import {
    OPEN_TOUR_AFTER_LOGIN_KEY,
    shouldAutoOpenTourForPersona,
    resolveDeliveryCockpitTourPersona,
} from '../constants/deliveryCockpitTourPersonas';

const DeliveryCockpitTourContext = createContext(null);

/**
 * Provider for delivery cockpit guided tour state.
 *
 * @param {{ children: React.ReactNode }} props
 */
export const DeliveryCockpitTourProvider = ({ children }) => {
    const { user } = useAuth();
    const { count: pendingApprovalCount } = usePendingTimesheetApprovalCount();
    const [isTourOpen, setIsTourOpen] = useState(false);
    const [autoOpenedThisSession, setAutoOpenedThisSession] = useState(false);

    const persona = useMemo(
        () => resolveDeliveryCockpitTourPersona(user?.role, { is_superuser: user?.is_superuser }),
        [user?.role, user?.is_superuser],
    );

    const tour = useDeliveryCockpitTour({
        userId: user?.id,
        role: user?.role,
        is_superuser: user?.is_superuser,
        firstName: user?.first_name,
        pendingApprovalCount,
        isOpen: isTourOpen,
    });

    useEffect(() => {
        if (
            tour.shouldAutoOpen
            && user?.id
            && !autoOpenedThisSession
            && shouldAutoOpenTourForPersona(persona)
        ) {
            tour.restoreProgress();
            setIsTourOpen(true);
            setAutoOpenedThisSession(true);
            markDeliveryCockpitTourSessionSeen();
            tour.markAutoOpened();
        }
    }, [
        tour.shouldAutoOpen,
        tour.restoreProgress,
        tour.markAutoOpened,
        user?.id,
        autoOpenedThisSession,
        persona,
    ]);

    useEffect(() => {
        if (!user?.id || typeof window === 'undefined') {
            return;
        }
        if (window.sessionStorage.getItem(OPEN_TOUR_AFTER_LOGIN_KEY) === '1') {
            window.sessionStorage.removeItem(OPEN_TOUR_AFTER_LOGIN_KEY);
            tour.startFullTour();
            setIsTourOpen(true);
            markDeliveryCockpitTourSessionSeen();
        }
    }, [user?.id, tour.startFullTour]);

    useEffect(() => {
        if (!user?.id) {
            setIsTourOpen(false);
            setAutoOpenedThisSession(false);
        }
    }, [user?.id]);

    const openFullTour = useCallback(() => {
        tour.startFullTour();
        setIsTourOpen(true);
    }, [tour]);

    const resumeTour = useCallback(() => {
        tour.resumeTour();
        setIsTourOpen(true);
    }, [tour]);

    const restartTour = useCallback(() => {
        tour.restartTour();
        setIsTourOpen(true);
    }, [tour]);

    const closeTour = useCallback(() => {
        setIsTourOpen(false);
    }, []);

    const dismissTour = useCallback((permanent = true) => {
        if (permanent) {
            tour.dismissTour();
        } else {
            tour.suppressTourForSession();
        }
        setIsTourOpen(false);
    }, [tour]);

    const value = useMemo(() => ({
        isTourOpen,
        openFullTour,
        resumeTour,
        restartTour,
        closeTour,
        hasSavedProgress: tour.hasSavedProgress,
        persona,
        ...tour,
        dismissTour,
    }), [
        isTourOpen,
        openFullTour,
        resumeTour,
        restartTour,
        closeTour,
        dismissTour,
        tour,
        persona,
    ]);

    return (
        <DeliveryCockpitTourContext.Provider value={value}>
            {children}
        </DeliveryCockpitTourContext.Provider>
    );
};

/**
 * Access delivery cockpit guided tour controls.
 *
 * @returns {object}
 */
export const useDeliveryCockpitTourContext = () => {
    const ctx = useContext(DeliveryCockpitTourContext);
    if (!ctx) {
        throw new Error('useDeliveryCockpitTourContext must be used within DeliveryCockpitTourProvider');
    }
    return ctx;
};

/** @deprecated Use useDeliveryCockpitTourContext */
export const useDeliveryCockpitHelp = () => {
    const ctx = useDeliveryCockpitTourContext();
    return {
        openHelp: ctx.openFullTour,
        openFullTour: ctx.openFullTour,
        closeHelp: ctx.closeTour,
        closeTour: ctx.closeTour,
        isHelpOpen: ctx.isTourOpen,
        isTourOpen: ctx.isTourOpen,
        dismissHelp: ctx.dismissTour,
        dismissTour: ctx.dismissTour,
        resumeTour: ctx.resumeTour,
        restartTour: ctx.restartTour,
        hasSavedProgress: ctx.hasSavedProgress,
    };
};

export const DeliveryCockpitHelpProvider = DeliveryCockpitTourProvider;
